import '../styles.css'
import React, { useEffect, useMemo, useState } from 'react'
import { Inter } from '@next/font/google'
import { ConfigProvider, message } from 'antd'
import { MedplumProvider } from '@medplum/react'
import type { ReactElement, ReactNode } from 'react'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advanced from 'dayjs/plugin/advancedFormat'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import isBetween from 'dayjs/plugin/isBetween'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import * as Sentry from '@sentry/nextjs'
import { FullStory, init as initFullstory } from '@fullstory/browser'
import Head from 'next/head'
import { QueryClientProvider } from '@tanstack/react-query'
import FeatureFlagProvider from 'providers/FeatureFlagProvider'
import FeatureFlagUserContextProvider from 'providers/FeatureFlagUserContextProvider'
import FeatureFlagPatientContext from 'contexts/FeatureFlagPatientContext'
import { medplum } from 'utils/medplumClient'
import { NotificationProvider } from 'contexts/NotificationContext'
import useSafeNavigation from 'hooks/useSafeNavigation'
import { queryClient } from 'clients/reactQuery'
import { captureException, SentryLevel, SentrySources } from 'utils/sentry'
import { useStore } from 'store'
import CheckAppVersionUpdate from 'components/CheckAppVersionUpdate'
import { OpenedAssessmentTabModal } from 'components/OpenedAssessmentTabModal'
import { SessionExpiredModal } from 'components/Common/SessionExpiredModal'
import { SESSION_RETURN_PATH_KEY } from 'constants/storage'
import favicon from '../public/assets/favicon.png'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)
// Needed for AntD Datepicker
// https://github.com/ant-design/ant-design/issues/26190#issuecomment-703673400
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(isBetween)
dayjs.extend(duration)
dayjs.extend(localizedFormat)
dayjs.extend(weekOfYear)
dayjs.extend(advancedFormat)

const inter = Inter({
  subsets: ['latin'],
  weight: ['100', '300', '400', '500', '700', '900']
})

const theme = {
  token: {
    fontFamily: inter.style.fontFamily,
    colorBgContainer: '#ffffff'
  }
}

// https://nextjs.org/docs/pages/building-your-application/routing/pages-and-layouts#with-typescript
export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const TOKEN_REFRESH_INTERVAL_MS = 30 * 60 * 1000 // 30 minutes
const TOKEN_REFRESH_TIMEOUT_MS = 2 * 60 * 1000 // 2 minutes
const TOKEN_REFRESH_GRACE_PERIOD_MS = 60 * 60 * 1000 // 60 minutes to avoid any gaps
const TOKEN_REFRESH_GRACE_PERIOD_JITTER_MS = 0.25 // 25% jitter

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const App = ({ Component, pageProps }: AppPropsWithLayout): any => {
  const { asPath } = useRouter()
  const { safeReplace } = useSafeNavigation()
  const isLoading = medplum.isLoading()
  const profile = medplum.getProfile()
  const [isMounted, setIsMounted] = useState(false)
  const [flagPatientId, setFlagPatientId] = useState<string | null>(null)

  // This is a workaround to avoid hydration errors.
  // We need to wait for the component to be mounted before rendering the app.
  // See: https://nextjs.org/docs/messages/react-hydration-error
  useEffect(() => {
    setIsMounted(true)
  }, [])

  const isWindow = typeof window !== 'undefined'

  // Q: Will this actually refresh? What happens if it attempts to refresh if expired, finds it's not expired, and then doesn't refresh until the token is completely gone?
  // A: TOKEN_REFRESH_GRACE_PERIOD_MS is a grace period until the token expires, Medplum token expiry time is 60 minutes, setting grace period to 60 minutes avoids any edge or corner case.

  // Q: Can we set a Timeout to refresh it 1 minute or 5 minutes or whatever before the token actually expires
  // A: There's no such information being exposed at the client side, we don't know when a token actually expires from client.

  // adding more context, If Remaining expiration time - grace period > 0, Medplum won’t issue a new token

  // More context for the jitter: https://abby-care.slack.com/archives/C0720R0KVTQ/p1727201619396209?thread_ts=1727201571.404069&cid=C0720R0KVTQ
  useEffect(() => {
    const handlePageLoad = async (): Promise<void> => {
      if (!medplum?.refreshIfExpired || medplum?.isLoading()) {
        return
      }

      try {
        await medplum.refreshIfExpired(TOKEN_REFRESH_GRACE_PERIOD_MS)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        const errorMessage = String(err?.message)
        const isTokenIssue = ['Invalid token', 'Token expired', 'Failed to fetch tokens'].some(
          (tokenIssue) => errorMessage.includes(tokenIssue)
        )

        const session = useStore.getState()?.session
        if (isTokenIssue && session?.isFeatureEnabled) {
          // Set session as expired in store - modal will handle it
          session.setExpired(true)
          // Still log to Sentry at INFO level for tracking
          captureException(err, {
            level: SentryLevel.INFO,
            tags: { source: SentrySources.MEDPLUM_TOKEN_ISSUE }
          })
          return // Don't throw - SessionExpiredModal will handle it
        }

        // Token issues with flag disabled, or non-token errors - use original behavior
        captureException(err, {
          level: isTokenIssue ? SentryLevel.INFO : SentryLevel.ERROR,
          tags: {
            source: isTokenIssue
              ? SentrySources.MEDPLUM_TOKEN_ISSUE
              : SentrySources.MEDPLUM_INITIALIZATION
          }
        })
        throw err
      }
    }

    const timeout =
      TOKEN_REFRESH_TIMEOUT_MS * (1 - Math.random() * TOKEN_REFRESH_GRACE_PERIOD_JITTER_MS)

    setTimeout(() => {
      void handlePageLoad()
    }, timeout)

    const refresh_interval =
      TOKEN_REFRESH_INTERVAL_MS * (1 - Math.random() * TOKEN_REFRESH_GRACE_PERIOD_JITTER_MS)

    const interval = setInterval(() => {
      void handlePageLoad()
    }, refresh_interval)

    // Cleanup the interval when component unmounts
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!isLoading && isWindow && !profile && asPath !== '/login') {
      // Store the current path in sessionStorage so we can redirect back after login
      // Only store if there isn't already a stored path
      // Use window.location.pathname + search instead of asPath to ensure we get the actual URL
      // (asPath can sometimes be the route pattern with brackets instead of the actual path)
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search
        if (currentPath && currentPath !== '/login') {
          const existingPath = sessionStorage.getItem(SESSION_RETURN_PATH_KEY)
          if (!existingPath) {
            sessionStorage.setItem(SESSION_RETURN_PATH_KEY, currentPath)
          }
        }
      }
      safeReplace('/login')
    }
  }, [isLoading, isWindow, profile, asPath, safeReplace])

  const profileUserEmail = profile?.telecom?.find((t) => t.system === 'email')?.value
  const practitionerId = profile?.id

  useEffect(() => {
    Sentry.setUser({ email: profileUserEmail, practitionerId })
  }, [profileUserEmail, practitionerId])

  const fullStoryOrgId = process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID

  useEffect(() => {
    if (fullStoryOrgId) {
      initFullstory({
        orgId: fullStoryOrgId,
        devMode: process.env.NODE_ENV !== 'production'
      })

      if (practitionerId && profileUserEmail) {
        FullStory('setIdentity', {
          uid: practitionerId.toString(),
          properties: {
            email: profileUserEmail
          }
        })
      }
    }
  }, [fullStoryOrgId, practitionerId, profileUserEmail])

  useEffect(() => {
    const showOfflineMessage = (): void => {
      void message.error('You are offline', 0) // persistent until manually closed or replaced
    }

    const showBackOnlineMessage = (): void => {
      message.destroy() // clear any previous messages
      void message.success('Back online', 3) // auto-dismiss in 3 seconds
    }

    const handleOnline = (): void => {
      showBackOnlineMessage()
    }

    const handleOffline = (): void => {
      showOfflineMessage()
    }

    const init = (): void => {
      if (!navigator.onLine) {
        showOfflineMessage()
      }
    }

    window.addEventListener('load', init)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('load', init)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const flagPatientContextValue = useMemo(
    () => ({ patientId: flagPatientId, setPatientId: setFlagPatientId }),
    [flagPatientId]
  )

  const getLayout = Component.getLayout ?? ((page) => page)

  // Parse the last part of the URL to use as the tab title.
  // Capitalize the first letter of each word except for acronyms like CNA.
  const { capitalizedTabTitle } = useMemo(() => {
    const tabTitle = asPath.split('/').pop()?.replace(/-/g, ' ')
    const capitalizedTabTitle = tabTitle?.replace(/cna|\b\w/g, (char) => char.toUpperCase())
    return { capitalizedTabTitle }
  }, [asPath])

  // This is a workaround to avoid hydration errors.
  // We need to wait for the component to be mounted before rendering the app.
  // See: https://nextjs.org/docs/messages/react-hydration-error
  if (!isMounted) {
    return null
  }

  return (
    <>
      <ConfigProvider theme={theme}>
        <MedplumProvider medplum={medplum}>
          <FeatureFlagProvider>
            <NotificationProvider>
              <QueryClientProvider client={queryClient}>
                <FeatureFlagPatientContext.Provider value={flagPatientContextValue}>
                  <FeatureFlagUserContextProvider>
                    <>
                      <Head>
                        <link rel="icon" href={favicon.src} />
                        <title>AbbyOS - {capitalizedTabTitle}</title>
                      </Head>
                      {getLayout(<Component {...pageProps} />)}
                    </>
                  </FeatureFlagUserContextProvider>
                </FeatureFlagPatientContext.Provider>
              </QueryClientProvider>
            </NotificationProvider>
            <SessionExpiredModal />
          </FeatureFlagProvider>
        </MedplumProvider>
      </ConfigProvider>
      <CheckAppVersionUpdate />
      <OpenedAssessmentTabModal />
    </>
  )
}

export default App
